import { applyConsentEvent, consentScopeMeta, type ConsentDecisionRequest, type ConsentEvent, type ConsentState } from '../domain/consent'
import type { DevicePrivacySettings, PrivacyJob, PrivacySnapshot } from '../domain/privacy'
import { compactWeeklySummary, emptyWeeklyCouncil, type PracticeLibraryView, type PracticePreferenceRequest, type SaveWeeklyCouncilRequest, type SaveWeeklyCouncilResult, type WeeklyCouncilView, type WeeklyInteractionForSummary } from '../domain/weekly'
import type { CandidatePublicationRequest, CandidateReviewRequest, CandidateRevisionRequest, CoachCandidateBundle, CoachProgressMemoryView, CoachProgressRunProjection, CoachReadinessPacket, RelationshipRunProjection } from '../domain/coach'
import type { ClientAssetProjection } from '../domain/types'
import type { DailyExperienceRequest, DailyInteractionRequest, DailyInteractionResult, DailySwapRequest } from '../domain/daily'
import { regenerateAsset, selectDailyPlan, starterAssets, type AssetResponse, type DailyPlan, type InteractionSignal } from '../domain/dailyEngine'
import type { FoundationCapacity, FoundationInteractionRequest, FoundationInteractionResult, FoundationView } from '../domain/foundation'
import { buildStarterSpine, profileFactFromOnboarding, type ProfileFactCorrectionRequest, type ProfileFactVersion, type RelationshipIntelligenceBundle, type SpineHypothesisReviewRequest } from '../domain/intelligence'
import { defaultNotificationSettings, type NotificationSettingsUpdate, type NotificationSettingsView, type NotificationTestResult, type PushInstallationRegistration } from '../domain/notifications'
import type { OfflineSyncReport } from '../domain/offline'
import {
  ONBOARDING_CHAPTERS,
  ONBOARDING_SCHEMA_VERSION,
  validateChapter,
  classifyOnboardingSafety,
  type OnboardingChapterSaveRequest,
  type OnboardingRestartRequest,
  type OnboardingRestartResult,
  type OnboardingSession,
  type OnboardingSubmissionResult,
} from '../domain/onboarding'
import type { PmmRepository } from './repository'

export class PrototypeRepository implements PmmRepository {
  readonly mode = 'prototype' as const
  private readonly responses = new Map<string, AssetResponse>()
  private readonly dailySignals: InteractionSignal[] = []
  private dailyPlan?: DailyPlan
  private readonly published = new Map<string, ClientAssetProjection>()
  private readonly practicePreferences = new Map<string, 'active' | 'pinned' | 'archived' | 'quarantined'>()
  private readonly weeklyCouncils = new Map<string, WeeklyCouncilView>()
  private consent: ConsentState = {
    version: 1,
    scopes: {
      core_service: {
        granted: true,
        lastEventId: 'prototype-core-service',
        decidedAt: new Date().toISOString(),
        noticeVersion: 'pmm-privacy-1'
      }
    }
  }
  private device: DevicePrivacySettings = { trustedDevice: false, privacyMode: true, persistentCacheActive: false }
  private notificationSettings = defaultNotificationSettings()
  private prototypeInstallations = 0
  private exportJob?: PrivacyJob
  private deletionJob?: PrivacyJob
  private intelligence?: RelationshipIntelligenceBundle
  private onboarding: OnboardingSession = {
    sessionId: 'onboarding-v1',
    schemaVersion: ONBOARDING_SCHEMA_VERSION,
    revision: 0,
    currentChapterId: 'orientation',
    completedChapterIds: [],
    state: 'in_progress',
    responses: {},
    updatedAt: new Date().toISOString(),
  }

  async getDailyExperience(request: DailyExperienceRequest): Promise<DailyPlan> {
    if (!this.dailyPlan || this.dailyPlan.localDate !== request.localDate || this.dailyPlan.capacity !== request.capacity) {
      this.dailyPlan = selectDailyPlan({ localDate: request.localDate, capacity: request.capacity, safetyTier: 'standard', hasPartner: true, hasChildren: true, relationshipPracticeConsent: true, fatherhoodPracticeConsent: true, interactionHistory: this.dailySignals }, starterAssets)
    }
    return structuredClone(this.dailyPlan)
  }

  async regenerateDailyAsset(request: DailySwapRequest): Promise<DailyPlan> {
    const current = await this.getDailyExperience(request)
    if (current.revision !== request.expectedRevision) throw new Error('daily_plan_revision_conflict')
    this.dailyPlan = regenerateAsset(current, request.assetId, { localDate: request.localDate, capacity: request.capacity, safetyTier: 'standard', hasPartner: true, hasChildren: true, relationshipPracticeConsent: true, fatherhoodPracticeConsent: true, interactionHistory: this.dailySignals }, starterAssets)
    this.dailySignals.push({ assetId: request.assetId, action: 'swapped', occurredAt: new Date().toISOString() })
    return structuredClone(this.dailyPlan)
  }

  async recordDailyInteraction(request: DailyInteractionRequest): Promise<DailyInteractionResult> {
    const occurredAt = new Date().toISOString()
    this.dailySignals.push({ assetId: request.assetId, action: request.action, feedback: request.feedback, occurredAt })
    if (request.value !== undefined && (request.action === 'draft' || request.action === 'completed')) {
      this.responses.set(request.assetId, { assetId: request.assetId, value: request.value, state: request.action, savedAt: occurredAt, planId: request.planId, localDate: request.localDate, feedback: request.feedback })
    }
    return { interactionId: request.clientMutationId, occurredAt }
  }

  async getPracticeLibrary(): Promise<PracticeLibraryView> {
    return {
      items: starterAssets.map((asset) => {
        const signals = this.dailySignals.filter((signal) => signal.assetId === asset.assetId)
        return { asset, preference: this.practicePreferences.get(asset.assetId) ?? 'active', stats: { offered: this.dailyPlan?.assets.some((item) => item.assetId === asset.assetId) ? 1 : 0, attempted: signals.filter((signal) => !['draft', 'swapped'].includes(signal.action)).length, completed: signals.filter((signal) => signal.action === 'completed').length, helpful: signals.filter((signal) => signal.feedback === 'helpful').length, poorFit: signals.filter((signal) => ['poor_fit', 'too_much', 'unsafe'].includes(signal.feedback ?? '')).length, lastInteractionAt: signals.at(-1)?.occurredAt ?? null } }
      }),
      availability: 'ready',
      updatedAt: new Date().toISOString(),
    }
  }

  async updatePracticePreference(request: PracticePreferenceRequest): Promise<PracticeLibraryView> {
    if (this.practicePreferences.get(request.assetId) === 'quarantined') throw new Error('Unsafe feedback requires review before restoration.')
    this.practicePreferences.set(request.assetId, request.state)
    return this.getPracticeLibrary()
  }

  async getWeeklyCouncil(weekId: string): Promise<WeeklyCouncilView> {
    const existing = this.weeklyCouncils.get(weekId)
    if (existing) return structuredClone(existing)
    const interactions: WeeklyInteractionForSummary[] = this.dailySignals.map((signal) => {
      const asset = starterAssets.find((item) => item.assetId === signal.assetId)!
      return { assetId: signal.assetId, localDate: signal.occurredAt.slice(0, 10), lane: asset?.lane ?? 'self', category: asset?.category ?? 'self_leadership', action: signal.action, feedback: signal.feedback, asksPartnerQuestion: asset?.asksPartnerQuestion }
    })
    const view: WeeklyCouncilView = { summary: compactWeeklySummary(weekId, interactions), review: emptyWeeklyCouncil(weekId), queue: { queueId: null, weekId, state: 'not_created', reasonCodes: [], meaningfulInteractions: 0, submittedAt: null } }
    this.weeklyCouncils.set(weekId, view)
    return structuredClone(view)
  }

  async saveWeeklyCouncil(request: SaveWeeklyCouncilRequest): Promise<SaveWeeklyCouncilResult> {
    const current = await this.getWeeklyCouncil(request.weekId)
    if (current.review.revision !== request.expectedRevision) throw new Error('weekly_review_revision_conflict')
    const now = new Date().toISOString()
    current.review = { ...current.review, state: request.state, revision: current.review.revision + 1, capacityNextWeek: request.capacityNextWeek, nextWeekPriority: request.nextWeekPriority, valueAlignment: request.valueAlignment, openLoopType: request.openLoopType, currentConstraint: request.currentConstraint, coachNote: request.coachNote, requestFreshPractices: request.requestFreshPractices, requestMoreTime: request.requestMoreTime === true, privateReflectionPresent: Boolean(request.privateReflection), privateReflectionAiInclusion: request.includePrivateReflectionInAi ? 'user_selected_pending_compaction' : 'excluded', ...(request.state === 'submitted' ? { submittedAt: now } : {}), updatedAt: now }
    if (request.state === 'submitted') current.queue = { queueId: 'prototype:' + request.weekId, weekId: request.weekId, state: !request.requestMoreTime && (current.summary.meaningfulInteractions >= 5 || request.requestFreshPractices) ? 'ready_for_coach' : 'held', reasonCodes: request.requestMoreTime ? ['man_requested_more_time_with_current_library'] : current.summary.meaningfulInteractions >= 5 || request.requestFreshPractices ? [] : ['five_meaningful_interactions_needed'], meaningfulInteractions: current.summary.meaningfulInteractions, submittedAt: now, requestMoreTime: request.requestMoreTime }
    this.weeklyCouncils.set(request.weekId, structuredClone(current))
    return { view: current, reused: false }
  }

  async getFoundationJourney(_capacity: FoundationCapacity): Promise<FoundationView> {
    throw new Error('The governed 16-Week Foundation requires Firebase mode.')
  }

  async recordFoundationInteraction(_request: FoundationInteractionRequest): Promise<FoundationInteractionResult> {
    throw new Error('The governed 16-Week Foundation requires Firebase mode.')
  }

  async saveResponse(response: AssetResponse): Promise<void> {
    this.responses.set(response.assetId, structuredClone(response))
  }

  async publishClientAssets(assets: ClientAssetProjection[]): Promise<void> {
    for (const asset of assets) this.published.set(asset.assetId, structuredClone(asset))
  }

  async listPublishedClientAssets(): Promise<ClientAssetProjection[]> {
    return Array.from(this.published.values()).map((asset) => structuredClone(asset))
  }

  async getCoachRelationshipReadiness(): Promise<CoachReadinessPacket> {
    return { consentCurrent: false, safetyTier: 'standard', ready: false, daysSinceLastRun: null, daysSinceLastPublication: null, usefulLibraryRunway: 0, reviewRecommendation: 'initial_generation', meaningfulInteractions: this.dailySignals.filter((item) => item.action === 'completed' || item.feedback).length, helpfulAssets: 0, poorFitAssets: 0, weeklyQueue: null, latestRun: null, initialRequestState: 'starter_pack_ready', initialGenerationPending: true, fullLibraryRefreshAvailable: false }
  }

  async getCoachProgressMemory(): Promise<CoachProgressMemoryView> {
    return { latest: null, history: [], latestRun: null, readiness: { sourceCount: 0, newEvidenceCount: 0, canRun: false } }
  }

  async startCoachProgressMemory(_clientMutationId: string): Promise<CoachProgressRunProjection> {
    throw new Error('Secure progress synthesis requires Firebase mode.')
  }

  async startRelationshipExpansion(_clientMutationId: string, _generationMode: 'standard' | 'full_library_refresh' = 'standard'): Promise<RelationshipRunProjection> {
    throw new Error('Secure CustomGhost generation requires Firebase mode.')
  }

  async getRelationshipRunStatus(_runId: string): Promise<RelationshipRunProjection> {
    throw new Error('Secure CustomGhost run status requires Firebase mode.')
  }

  async getCoachCandidateSet(_candidateSetId: string): Promise<CoachCandidateBundle> {
    throw new Error('Secure coach candidates require Firebase mode.')
  }

  async saveCoachCandidateRevision(_request: CandidateRevisionRequest): Promise<void> {
    throw new Error('Secure candidate revision requires Firebase mode.')
  }

  async reviewCoachCandidate(_request: CandidateReviewRequest): Promise<void> {
    throw new Error('Secure candidate review requires Firebase mode.')
  }

  async publishCoachCandidateSet(_request: CandidatePublicationRequest): Promise<{ publishedCount: number }> {
    throw new Error('Secure candidate publication requires Firebase mode.')
  }

  async getPrivacySnapshot(): Promise<PrivacySnapshot> {
    return structuredClone({ consent: this.consent, device: this.device, exportJob: this.exportJob, deletionJob: this.deletionJob })
  }

  async acceptCoreService(): Promise<ConsentState> {
    return structuredClone(this.consent)
  }

  async recordConsentDecision(input: ConsentDecisionRequest): Promise<ConsentState> {
    const meta = consentScopeMeta[input.scope]
    const event: ConsentEvent = {
      eventId: crypto.randomUUID(),
      ownerUid: 'prototype-owner',
      scope: input.scope,
      decision: input.decision,
      granted: input.decision === 'grant',
      noticeVersion: 'pmm-privacy-1',
      policyVersion: 'pmm-policy-1',
      dataCategories: meta.dataCategories,
      processorCategories: meta.processorCategories,
      retentionChoice: 'scope_default',
      previousEventId: input.previousEventId,
      occurredAt: new Date().toISOString(),
      uiSource: input.uiSource
    }
    this.consent = applyConsentEvent(this.consent, event)
    if (input.scope === 'offline_persistent_cache') this.device.persistentCacheActive = event.granted && this.device.trustedDevice
    if (input.scope === 'push_notifications' && !event.granted) this.notificationSettings.enabled = false
    return structuredClone(this.consent)
  }

  async updateDevicePrivacy(settings: Pick<DevicePrivacySettings, 'trustedDevice' | 'privacyMode'>): Promise<DevicePrivacySettings> {
    this.device = { ...settings, persistentCacheActive: settings.trustedDevice && this.consent.scopes.offline_persistent_cache?.granted === true }
    return structuredClone(this.device)
  }

  async requestExport(): Promise<PrivacyJob> {
    this.exportJob = { jobId: crypto.randomUUID(), kind: 'export', state: 'requested', requestedAt: new Date().toISOString() }
    return structuredClone(this.exportJob)
  }

  async requestDeletion(): Promise<PrivacyJob> {
    this.deletionJob = { jobId: crypto.randomUUID(), kind: 'deletion', state: 'requested', requestedAt: new Date().toISOString() }
    return structuredClone(this.deletionJob)
  }

  async clearSensitiveDeviceState(): Promise<void> {
    this.responses.clear()
  }

  async getOnboardingSession(): Promise<OnboardingSession> {
    return structuredClone(this.onboarding)
  }
  async syncOfflineQueue(): Promise<OfflineSyncReport> {
    return { synced: 0, pending: 0, conflicts: 0 }
  }

  async getNotificationSettings(): Promise<NotificationSettingsView> {
    return structuredClone({ ...this.notificationSettings, activeInstallationCount: this.prototypeInstallations })
  }

  async updateNotificationSettings(settings: NotificationSettingsUpdate): Promise<NotificationSettingsView> {
    this.notificationSettings = { ...settings, preview: 'neutral', activeInstallationCount: this.prototypeInstallations, schemaVersion: 'pmm-notification-settings-1' }
    return this.getNotificationSettings()
  }

  async registerPushInstallation(_input: PushInstallationRegistration): Promise<NotificationSettingsView> {
    this.prototypeInstallations = 1
    return this.getNotificationSettings()
  }

  async unregisterPushInstallation(_installationId: string): Promise<NotificationSettingsView> {
    this.prototypeInstallations = 0
    this.notificationSettings.enabled = false
    return this.getNotificationSettings()
  }

  async sendTestNotification(): Promise<NotificationTestResult> {
    return { state: this.notificationSettings.enabled && this.prototypeInstallations ? 'sent' : 'disabled', successfulDeliveries: this.notificationSettings.enabled && this.prototypeInstallations ? 1 : 0 }
  }

  async saveOnboardingChapter(request: OnboardingChapterSaveRequest): Promise<OnboardingSession> {
    if (this.onboarding.state === 'submitted') return structuredClone(this.onboarding)
    if (request.sessionId !== this.onboarding.sessionId || request.expectedRevision !== this.onboarding.revision) {
      throw new Error('onboarding_revision_conflict')
    }
    const merged = { ...this.onboarding.responses }
    for (const response of request.responses) merged[response.fieldId] = structuredClone(response)
    const completed = !request.markComplete || this.onboarding.completedChapterIds.includes(request.chapterId)
      ? this.onboarding.completedChapterIds
      : [...this.onboarding.completedChapterIds, request.chapterId]
    this.onboarding = {
      ...this.onboarding,
      revision: this.onboarding.revision + 1,
      currentChapterId: request.nextChapterId,
      completedChapterIds: completed,
      responses: merged,
      updatedAt: new Date().toISOString(),
    }
    return structuredClone(this.onboarding)
  }

  async submitOnboarding(sessionId: string, expectedRevision: number): Promise<OnboardingSubmissionResult> {
    if (this.onboarding.state === 'submitted' && this.onboarding.snapshotId) {
      return { snapshotId: this.onboarding.snapshotId, session: structuredClone(this.onboarding), generationState: 'starter_pack_ready', safetyTier: classifyOnboardingSafety(this.onboarding.responses) }
    }
    if (sessionId !== this.onboarding.sessionId || expectedRevision !== this.onboarding.revision) throw new Error('onboarding_revision_conflict')
    const missing = ONBOARDING_CHAPTERS.flatMap((chapter) => Object.keys(validateChapter(chapter.id, this.onboarding.responses)))
    if (missing.length > 0) throw new Error(`Complete the required baseline answers before submitting (${missing.length} missing).`)
    const snapshotId = `prototype-${this.onboarding.revision}`
    this.onboarding = {
      ...this.onboarding,
      state: 'submitted',
      submittedAt: new Date().toISOString(),
      snapshotId,
      revision: this.onboarding.revision + 1,
      updatedAt: new Date().toISOString(),
    }
    return { snapshotId, session: structuredClone(this.onboarding), generationState: 'starter_pack_ready', safetyTier: classifyOnboardingSafety(this.onboarding.responses) }
  }

  async restartOnboardingTest(request: OnboardingRestartRequest): Promise<OnboardingRestartResult> {
    if (request.sessionId !== this.onboarding.sessionId || request.expectedRevision !== this.onboarding.revision) throw new Error('onboarding_revision_conflict')
    if (request.confirmation !== 'RESTART ONBOARDING') throw new Error('restart_confirmation_required')
    if (this.onboarding.state !== 'submitted' || !this.onboarding.snapshotId) throw new Error('Complete the baseline before restarting the onboarding test.')
    const archivedSnapshotId = this.onboarding.snapshotId
    const archivedResponseCount = Object.values(this.onboarding.responses).filter((response) => response.sensitivity !== 'high').length
    const archivedSensitiveResponseCount = Object.values(this.onboarding.responses).filter((response) => response.sensitivity === 'high').length
    const archiveId = `prototype-${request.clientMutationId}`
    this.onboarding = {
      sessionId: this.onboarding.sessionId,
      schemaVersion: ONBOARDING_SCHEMA_VERSION,
      revision: this.onboarding.revision + 1,
      currentChapterId: 'orientation',
      completedChapterIds: [],
      state: 'in_progress',
      responses: {},
      updatedAt: new Date().toISOString(),
    }
    return {
      archiveId,
      archivedSnapshotId,
      archivedResponseCount,
      archivedSensitiveResponseCount,
      session: structuredClone(this.onboarding),
      preservedData: { immutableSnapshots: true, relationshipIntelligence: true, generatedAssets: true, journalsAndInteractions: true },
    }
  }

  async getRelationshipIntelligence(): Promise<RelationshipIntelligenceBundle> {
    if (!this.intelligence) {
      const now = new Date().toISOString()
      const facts = Object.values(this.onboarding.responses)
        .map((response) => profileFactFromOnboarding('prototype-owner', response, now))
        .filter((fact): fact is NonNullable<typeof fact> => Boolean(fact))
      const snapshotId = this.onboarding.snapshotId ?? 'prototype-baseline'
      const spine = buildStarterSpine('prototype-owner', snapshotId, facts, now)
      const factHistory: ProfileFactVersion[] = facts.map((fact) => ({
        ...fact,
        versionId: fact.currentVersionId,
        mutationId: `starter:${snapshotId}`,
      }))
      this.intelligence = { facts, factHistory, spine }
    }
    return structuredClone(this.intelligence)
  }

  async correctProfileFact(request: ProfileFactCorrectionRequest): Promise<RelationshipIntelligenceBundle> {
    const bundle = await this.getRelationshipIntelligence()
    const index = bundle.facts.findIndex((fact) => fact.factId === request.factId)
    if (index < 0) throw new Error('Profile fact not found.')
    const current = bundle.facts[index]
    if (current.revision !== request.expectedRevision) throw new Error('profile_fact_revision_conflict')
    if (current.state === 'revoked') throw new Error('A revoked fact cannot be changed.')
    const now = new Date().toISOString()
    const revision = current.revision + 1
    const versionId = `${current.factId}:v${revision}:${request.clientMutationId.slice(0, 8)}`
    const next = {
      ...current,
      value: request.action === 'correct' ? request.replacementValue! : current.value,
      state: request.action === 'revoke' ? 'revoked' as const : request.action === 'dispute' ? 'disputed' as const : 'active' as const,
      revision,
      currentVersionId: versionId,
      effectiveAt: now,
    }
    bundle.facts[index] = next
    bundle.factHistory.push({ ...next, versionId, supersedesVersionId: current.currentVersionId, correctionReason: request.reason, mutationId: request.clientMutationId })
    const keep = request.action === 'correct'
    const filter = (ids: string[]) => keep ? ids : ids.filter((id) => id !== current.factId)
    bundle.spine = {
      ...bundle.spine,
      spineVersionId: `prototype-spine-${bundle.spine.versionNumber + 1}`,
      versionNumber: bundle.spine.versionNumber + 1,
      parentSpineVersionId: bundle.spine.spineVersionId,
      directFactIds: filter(bundle.spine.directFactIds),
      strengthFactIds: filter(bundle.spine.strengthFactIds),
      priorityFactIds: filter(bundle.spine.priorityFactIds),
      constraintFactIds: filter(bundle.spine.constraintFactIds),
      partnerReportFactIds: filter(bundle.spine.partnerReportFactIds),
      hypotheses: bundle.spine.hypotheses.map((hypothesis) => hypothesis.sourceFactIds.includes(current.factId) ? {
        ...hypothesis,
        state: 'invalidated' as const,
        invalidationReason: request.action === 'revoke' ? 'source_fact_revoked' as const : 'source_fact_changed' as const,
      } : hypothesis),
      invalidatedFactIds: Array.from(new Set([...bundle.spine.invalidatedFactIds, current.factId])),
      userReviewState: 'needs_review',
      createdAt: now,
    }
    this.intelligence = structuredClone(bundle)
    return bundle
  }

  async reviewSpineHypothesis(request: SpineHypothesisReviewRequest): Promise<RelationshipIntelligenceBundle> {
    const bundle = await this.getRelationshipIntelligence()
    if (bundle.spine.spineVersionId !== request.spineVersionId) throw new Error('relationship_spine_revision_conflict')
    const hypothesis = bundle.spine.hypotheses.find((item) => item.hypothesisId === request.hypothesisId)
    if (!hypothesis) throw new Error('Hypothesis not found.')
    const now = new Date().toISOString()
    bundle.spine = {
      ...bundle.spine,
      spineVersionId: `prototype-spine-${bundle.spine.versionNumber + 1}`,
      versionNumber: bundle.spine.versionNumber + 1,
      parentSpineVersionId: bundle.spine.spineVersionId,
      userReviewState: 'reviewed',
      createdAt: now,
      hypotheses: bundle.spine.hypotheses.map((item) => item.hypothesisId === request.hypothesisId ? {
        ...item,
        state: request.decision === 'reject' ? 'rejected' as const : request.decision === 'confirm_for_now' ? 'user_confirmed_hypothesis' as const : 'tentative' as const,
        ...(request.decision === 'edit' ? { userEditedStatement: request.editedStatement } : {}),
        expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      } : item),
    }
    this.intelligence = structuredClone(bundle)
    return bundle
  }
}

// Prototype mode is deliberately memory-only. Firebase mode selects the
// Firestore adapter, whose data paths are protected by LOOP-02 rules.
export const prototypeRepository = new PrototypeRepository()
